import React, { useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Table from 'react-bootstrap/Table'
import ApplyPanel from './ApplyPanel'
import Pagination from '../../../component/Pagination'
import useDecisions from './useDecisions'

// Strips the course's identity off its layouts' names: "A6 Jkpg" under course "A6"
// becomes "Jkpg", and a layout named after its course and nothing else becomes
// "Main".
//
// This runs last, after the course passes. Every layout name here is derived from
// its course's name, so running it against course names that are themselves wrong
// bakes the error into thousands of layout names.
//
// One pass is enough: every decision looks at one layout in isolation. An empty
// automatic bucket is the convergence signal — and the same number is the drift
// signal later, because the legacy v4 sync path lets an old install push a
// prefixed name back over a cleaned one.
//
// ## Why course names are editable here
// The layouts are where a wrong course name becomes visible: you cannot tell that
// "A6" should be "A6 Jönköping" until you are looking at the six layouts hanging
// off it that all say so. Sending somebody to the names tab to act on what they are
// reading here loses the evidence on the way. So the course name is editable in
// place and submitted to the course-name endpoint — the same one the names tab
// posts to, with the same validation and the same staleness check. It is a second
// request, not a second kind of write.
const BUCKETS = [
  {
    key: 'autoApply',
    label: 'Automatic',
    hint: 'Nothing to decide: the layout was named after its course and nothing else, or the tail is a known descriptor.',
  },
  {
    key: 'review',
    label: 'Review',
    hint: 'A real layout name that needs a human to confirm.',
  },
  {
    key: 'mismatched',
    label: 'Unrelated to course',
    hint: 'The name has nothing to do with the course it hangs off, which usually means it was grouped onto the wrong course. A grouping question, never a layout-naming one — so the layout name is not editable here. The course name is: sometimes the layouts are right and the course is the thing that is wrong.',
  },
]

export default function LayoutsPass({ country, data, onDryRun, onApply, applying, dryRunSeen }) {
  const [bucket, setBucket] = useState('autoApply')
  const [page, setPage] = useState(0)
  const { decisions, setDecision, decidedCount, clear } = useDecisions('layouts', country)
  // A separate draft, because these are course decisions and go to a different
  // endpoint. Keyed by course id, so several layout rows under one course edit one
  // value — which is also what stops the server seeing the same course twice in one
  // batch and rejecting both copies as a duplicate.
  const courseNames = useDecisions('layout-course-names', country)

  const rows = useMemo(() => (data ? data[bucket] || [] : []), [data, bucket])

  // Current course names, from every row the payload carries. The candidates list is
  // read too: it can name a course whose layouts all sit in a bucket you are not on.
  const courseNameById = useMemo(() => {
    const map = new Map()
    if (!data) return map
    const all = [...data.autoApply, ...data.review, ...data.mismatched]
    all.forEach((row) => map.set(String(row.courseId), row.courseName))
    data.courseNameCandidates.forEach((c) => {
      if (!map.has(String(c.courseId))) map.set(String(c.courseId), c.courseName)
    })
    return map
  }, [data])

  if (!data) return null

  const editable = bucket !== 'mismatched'

  const decisionFor = (row) => decisions[row.layoutId] ?? { newName: row.proposedName || '' }

  const currentCourseName = (courseId) => courseNameById.get(String(courseId)) ?? ''

  // The staged course name, or the one it has now.
  const courseNameFor = (courseId) =>
    courseNames.decisions[courseId]?.newName ?? currentCourseName(courseId)

  // A decision is only kept while it differs from the live name: typing a name back
  // to what it was is a cancelled edit, not a rename to submit.
  const setCourseName = (courseId, value) => {
    const current = currentCourseName(courseId)
    courseNames.setDecision(courseId, value === current ? undefined : { newName: value })
  }

  const courseRenames = Object.entries(courseNames.decisions)
    .map(([courseId, value]) => ({
      courseId: Number(courseId),
      oldName: currentCourseName(courseId),
      newName: (value?.newName ?? '').trim(),
    }))
    // A blank box means "leave it alone", the same as it does in the layout column.
    // Sending it would be a rename to nothing, which the server rejects as invalid —
    // and a rejection nobody asked for reads as a bug rather than as a refusal.
    .filter((r) => r.newName && r.newName !== r.oldName)

  const acceptAllShown = () =>
    rows.forEach((row) => {
      if (row.proposedName) setDecision(row.layoutId, { newName: row.proposedName })
    })

  const clearShown = () => rows.forEach((row) => setDecision(row.layoutId, undefined))

  const clearAll = () => {
    clear()
    courseNames.clear()
  }

  const submit = (dryRun) => {
    const all = [...data.autoApply, ...data.review, ...data.mismatched]
    const layouts = Object.entries(decisions).map(([layoutId, value]) => {
      const row = all.find((r) => String(r.layoutId) === String(layoutId))
      return {
        layoutId: Number(layoutId),
        courseId: row ? row.courseId : null,
        oldName: row ? row.oldName : '',
        newName: value.newName,
      }
    })
    // newSecondaryName is deliberately never sent with the course renames: omitting
    // it means "leave the existing qualifier alone", and nothing on this tab is a
    // judgement about the qualifier.
    const parts = { layouts, courseNames: courseRenames }
    if (dryRun) onDryRun(parts)
    else onApply(parts, clearAll)
  }

  return (
    <>
      <div className="curation-counts mb-2">
        <Badge bg="secondary" className="me-2">considered {data.counts.considered}</Badge>
        <Badge bg="secondary" className="me-2">automatic {data.counts.autoApply}</Badge>
        <Badge bg="secondary" className="me-2">review {data.counts.review}</Badge>
        <Badge bg="light" text="dark" className="me-2">unrelated {data.counts.mismatched}</Badge>
        <Badge bg="light" text="dark">no course identity {data.counts.skippedCourses}</Badge>
      </div>

      <Nav variant="pills" activeKey={bucket} onSelect={(k) => { setBucket(k); setPage(0) }}>
        {BUCKETS.map((b) => (
          <Nav.Item key={b.key}>
            <Nav.Link eventKey={b.key}>
              {b.label} <Badge bg="light" text="dark">{(data[b.key] || []).length}</Badge>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <p className="text-muted small mt-2">{BUCKETS.find((b) => b.key === bucket)?.hint}</p>

      <div className="d-flex gap-2 mb-2 align-items-center flex-wrap">
        {editable && (
          <>
            <Button size="sm" variant="outline-primary" onClick={acceptAllShown}>
              Accept all {rows.length} shown
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={clearShown}>
              Clear
            </Button>
          </>
        )}
        {courseRenames.length > 0 && (
          <span className="text-muted small">
            <strong>{courseRenames.length}</strong>{' '}
            {courseRenames.length === 1 ? 'course' : 'courses'} also staged for rename
            <Button
              size="sm"
              variant="link"
              className="p-0 ms-2 align-baseline"
              onClick={courseNames.clear}
            >
              clear those
            </Button>
          </span>
        )}
      </div>

      <Pagination items={rows} page={page} onPage={setPage}>
        {(shown) => (
          <Table size="sm" responsive hover className="curation-table">
            <thead>
              <tr>
                <th>Layout</th>
                <th>Course</th>
                <th>Course name</th>
                <th>Current name</th>
                {editable && <th>New layout name</th>}
                <th>Reason</th>
                <th>Holes</th>
                <th>Current?</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const decision = decisionFor(row)
                const courseRenamed = courseNames.decisions[row.courseId] !== undefined
                const decided = decisions[row.layoutId] !== undefined || courseRenamed
                return (
                  <tr key={row.layoutId} className={decided ? 'table-active' : undefined}>
                    <td>{row.layoutId}</td>
                    <td className="text-muted">#{row.courseId}</td>
                    <td className="curation-name-cell">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        className={courseRenamed ? 'curation-course-renamed' : undefined}
                        value={courseNameFor(row.courseId)}
                        title={
                          courseRenamed
                            ? `Renames the course from "${currentCourseName(row.courseId)}". Every layout under it moves with it.`
                            : 'Renames the course itself, not this layout. Applies to every layout under it.'
                        }
                        onChange={(e) => setCourseName(row.courseId, e.target.value)}
                      />
                    </td>
                    <td>{row.oldName}</td>
                    {editable && (
                      <td className="curation-name-cell">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={decision.newName}
                          placeholder="blank to leave alone"
                          onChange={(e) =>
                            setDecision(row.layoutId, { newName: e.target.value })
                          }
                        />
                      </td>
                    )}
                    <td className="small curation-secondary-col">{row.reason}</td>
                    <td>{row.holeCount}</td>
                    {/* Superseded layouts are renamed too: Game.courseName renders the
                        layout name on every round ever played, so leaving them alone
                        would keep showing the old name in exactly that history. */}
                    <td>{row.isCurrent ? 'yes' : <span className="text-muted">superseded</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Pagination>

      {data.courseNameCandidates.length > 0 && (
        <Alert variant="light" className="small">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <span>
              <strong>{data.courseNameCandidates.length} courses</strong> have a layout
              holding a fuller version of the <em>course</em> name. The layout is renamed
              with the rest; the course rename is a suggestion, so it is never staged
              until you say so.
            </span>
            <Button
              size="sm"
              variant="outline-primary"
              className="flex-shrink-0 ms-2"
              onClick={() =>
                data.courseNameCandidates.forEach((c) => setCourseName(c.courseId, c.candidate))
              }
            >
              Use all {data.courseNameCandidates.length}
            </Button>
          </div>
          <ul className="mb-0 list-unstyled">
            {data.courseNameCandidates.map((c) => {
              const staged = courseNames.decisions[c.courseId]?.newName === c.candidate
              return (
                <li key={c.courseId} className="mb-1">
                  <Button
                    size="sm"
                    variant={staged ? 'success' : 'outline-secondary'}
                    className="me-2"
                    onClick={() =>
                      setCourseName(c.courseId, staged ? currentCourseName(c.courseId) : c.candidate)
                    }
                  >
                    {staged ? 'Staged' : 'Use this name'}
                  </Button>
                  #{c.courseId} {c.courseName} → <strong>{c.candidate}</strong>
                </li>
              )
            })}
          </ul>
        </Alert>
      )}

      <ApplyPanel
        decidedCount={decidedCount + courseRenames.length}
        total={data.counts.considered}
        dryRunSeen={dryRunSeen}
        applying={applying}
        applyLabel={
          courseRenames.length > 0
            ? `Apply ${decidedCount} layout and ${courseRenames.length} course renames`
            : undefined
        }
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
